import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ToggleSwitch,
} from 'flowbite-react';
import { useState } from 'react';
import type { LicenseDto } from '../../queries/use-licenses.js';
import {
  useTriggerLicenseActions,
  type TriggerActionResult,
} from '../../queries/use-trigger-license-actions.js';

/**
 * Trigger System Action modal — operator-side fan-out of engine
 * orchestration calls for a given license. Port of the legacy
 * `wf-adminconsole/components/dashboard/trigger/TriggerActionModal.tsx`.
 *
 * Differences from legacy:
 *   - 4 toggles instead of 6 — the engine fans out internally from
 *     `main-queue`, so the legacy "scenario backup" and "AI summary"
 *     toggles collapse into "Trigger main queue (orchestrator)".
 *     Per-downstream-queue triggers (fusion-metrics / screenshot-
 *     capture / github-backup) stay separate so operators can run
 *     them without disturbing the rest of the pipeline.
 *   - Per-action result row shown after submit. Lets operators see
 *     which queues accepted the job and which failed (e.g. a tenant
 *     that hasn't onboarded screenshot-capture credentials yet).
 *
 * Auth: `/v1/admin/triggers/{licenseId}` is Casbin-gated on
 * `system:write` (Admin role).
 */
export function TriggerActionModal({
  isOpen,
  onClose,
  license,
}: {
  isOpen: boolean;
  onClose: () => void;
  license: LicenseDto;
}): JSX.Element {
  const [mainQueue, setMainQueue] = useState(true);
  const [fusionMetrics, setFusionMetrics] = useState(false);
  const [screenshotCapture, setScreenshotCapture] = useState(false);
  const [githubBackup, setGithubBackup] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(false);

  const trigger = useTriggerLicenseActions();

  const anySelected = mainQueue || fusionMetrics || screenshotCapture || githubBackup;

  function handleClose(): void {
    if (trigger.isPending) return;
    trigger.reset();
    onClose();
  }

  async function submit(): Promise<void> {
    await trigger
      .mutateAsync({
        licenseId: license.id,
        mainQueue,
        fusionMetrics,
        screenshotCapture,
        githubBackup,
        forceRefresh,
      })
      .catch(() => {
        // Error surfaces via trigger.error.
      });
  }

  return (
    <Modal size="md" show={isOpen} onClose={handleClose}>
      <ModalHeader>Trigger System Action</ModalHeader>
      <ModalBody className="flex flex-col gap-3">
        <p className="text-xs text-gray-600 dark:text-gray-400">
          License <span className="font-mono">{license.id.slice(0, 8)}…</span> ·{' '}
          <span className="uppercase">{license.type}</span>
        </p>

        <ToggleRow
          id="trigger-main-queue"
          label="Trigger main-queue orchestrator"
          description="Fans out to scenarios + Workfront backup + AI summary downstream."
          checked={mainQueue}
          onChange={setMainQueue}
        />
        <ToggleRow
          id="trigger-fusion-metrics"
          label="Trigger Fusion metrics collection"
          description="Enqueues a per-license Fusion-API logs pull."
          checked={fusionMetrics}
          onChange={setFusionMetrics}
        />
        <ToggleRow
          id="trigger-screenshot-capture"
          label="Trigger screenshot capture"
          description="Headless-browser capture of each scenario detail page."
          checked={screenshotCapture}
          onChange={setScreenshotCapture}
        />
        <ToggleRow
          id="trigger-github-backup"
          label="Trigger GitHub backup"
          description="Commits the per-license scenario state to the tenant's GitHub backup repo."
          checked={githubBackup}
          onChange={setGithubBackup}
        />

        <div className="mt-2 border-t border-gray-200 pt-3 dark:border-gray-700">
          <ToggleRow
            id="trigger-force-refresh"
            label="Force refresh"
            description="Bypass downstream caches. Use sparingly — multiplies engine load."
            checked={forceRefresh}
            onChange={setForceRefresh}
            danger
          />
        </div>

        {trigger.error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
            {trigger.error.message}
          </div>
        ) : null}

        {trigger.data ? (
          <div className="rounded-md border border-gray-200 dark:border-gray-700">
            <header className="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold dark:border-gray-700 dark:bg-gray-800">
              Result
            </header>
            <ul className="divide-y divide-gray-100 text-xs dark:divide-gray-800">
              {trigger.data.results.map((r) => (
                <ResultRow key={r.action} result={r} />
              ))}
            </ul>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="gray" onClick={handleClose} disabled={trigger.isPending}>
          Close
        </Button>
        <Button
          color="blue"
          onClick={() => void submit()}
          disabled={!anySelected || trigger.isPending}
        >
          {trigger.isPending ? 'Triggering…' : 'Trigger'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function ToggleRow({
  id,
  label,
  description,
  checked,
  onChange,
  danger,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  danger?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1">
        <Label
          htmlFor={id}
          className={`font-normal ${danger ? 'text-red-600 dark:text-red-400' : ''}`}
        >
          {label}
        </Label>
        <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      <ToggleSwitch
        id={id}
        color={danger ? 'red' : 'green'}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
}

function ResultRow({ result }: { result: TriggerActionResult }): JSX.Element {
  return (
    <li className="flex items-center justify-between px-3 py-1.5">
      <span className="font-mono">{result.action}</span>
      <span
        className={
          result.ok ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
        }
      >
        {result.ok ? '✓' : '✗'} {result.status || result.error || 'failed'}
      </span>
    </li>
  );
}
